import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon, SelectorIcon } from "@heroicons/react/solid";
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { classNames } from "../../../helpers";
import UserProfilePictureAvatar from "../../avatar/UserProfilePictureAvatar";

export type DropDownOption = { key: string; value: any; image?: string };

type dropdownProps = {
  id?: string;
  name?: string;
  schema?: any;
  register?: Function;
  defaultValue: any;
  setValue: Function;
  onBottomReached?: () => void;
  options: DropDownOption[];
  label?: string;
  error?: boolean;
  placeholder?: string;
  CustomInput?: React.FC<{
    options: DropDownOption[];
    value: any;
  }>;
  className?: string;
  asSelector?: boolean;
  disabled?: boolean;
  bottomReachedMargin?: number;
  cypressSelector?: string;
  hasImage?: boolean;
  subLabel?: string;
};
export default function DropDown(props: dropdownProps) {
  const {
    id,
    name,
    schema = {},
    register = () => {},
    defaultValue,
    setValue,
    options,
    label,
    error,
    placeholder,
    CustomInput,
    className,
    asSelector,
    disabled,
    onBottomReached,
    bottomReachedMargin,
    cypressSelector,
    hasImage,
    subLabel,
  } = props;
  const [currentValue, setCurrentValue] = useState<any>(defaultValue);
  const [selectedOption, setSelectedOption] =
    useState<DropDownOption | undefined>();
  useEffect(() => {
    setCurrentValue(defaultValue);
    const selectedOption = options.find(
      (option) => option.value === defaultValue
    );
    setSelectedOption(selectedOption);
  }, [defaultValue, options]);

  const onClickItem = (value: any) => {
    setValue(value);
    setCurrentValue(value);
  };
  const [filterContainerRef, setFilterContainerRef] =
    useState<HTMLDivElement>();

  /**
   *  why I 've not used useRef
   *  useRef is set to the DOM node when it’s mounted and set to null when the DOM node is removed or not rendered.
   *  in our case filter dropdown is unmounted until the condition happens, after that it will be rendered
   *  so when we use ref it will be null all the time
   *  useRef is, strictly speaking, never updated If a DOM node is replaced by some other node say, its DOM tag or key changes  or not mounted yet
   *
   */
  const filterRef = useCallback((e: any) => setFilterContainerRef(e), []);

  const onScrollHandler = () => {
    /**
     * checking if ref is not null
     */
    if (!!filterContainerRef) {
      const { clientHeight, scrollTop, scrollHeight } = filterContainerRef;
      const bottom: boolean =
        Math.floor(scrollHeight - scrollTop) <= filterContainerRef.clientHeight;
      const bottomMargin: boolean =
        scrollTop + clientHeight >= scrollHeight - (bottomReachedMargin ?? 0);

      if (
        ((!!bottomReachedMargin && bottomMargin) || bottom) &&
        !!onBottomReached
      ) {
        onBottomReached();
      }
    }
  };

  const isRequired = useMemo(() => {
    if (!!schema) {
      return !!schema.required;
    }
    return false;
  }, [schema]);

  const DropDownIcon = !!asSelector ? SelectorIcon : ChevronDownIcon;

  useLayoutEffect(() => {
    // Decide if the dropdown should be open to the top or bottom
    if (filterContainerRef) {
      const windowHeight = window.innerHeight;
      const { top, height } = filterContainerRef.getBoundingClientRect();
      const bottom = windowHeight - top;
      if (height + top > windowHeight && top > bottom) {
        filterContainerRef.classList.add("-translate-y-[calc(100%+3.1rem)]");
      } else {
        filterContainerRef.classList.remove("-translate-y-[calc(100%+3.1rem)]");
      }
    }
  }, [filterContainerRef]);

  return (
    <Menu as="div" className="relative inline-block text-start w-full">
      <div className={`${className}`}>
        {!!label && (
          <label
            htmlFor={id}
            className={"block text-sm font-medium text-gray-700 mb-1"}
          >
            {`${label}`}

            {!!isRequired ? " *" : ""}
          </label>
        )}
        {!!CustomInput ? (
          <Menu.Button type="button">
            <CustomInput options={options} value={currentValue} />
          </Menu.Button>
        ) : (
          <Menu.Button
            type="button"
            id={id}
            {...register(name, schema)}
            className={classNames(
              "inline-flex justify-between w-full items-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-900 focus:outline-none",
              !!disabled
                ? "bg-gray-100 cursor-default"
                : "hover:bg-gray-50 focus:ring-1 focus:ring-offset-gray-100 focus:ring-brandblue-500",
              error &&
                "ring-1 ring-red-600 focus:ring-red-600 focus:border-red-600",
              hasImage && "gap-3"
            )}
            title={selectedOption?.key || label}
            data-cy-value={selectedOption?.value}
            data-cy={cypressSelector}
          >
            {!!selectedOption ? (
              <>
                {!!hasImage && (
                  <UserProfilePictureAvatar
                    profilePictureUrl={selectedOption.image}
                    className="w-6 h-6 flex-shrink-0"
                    alt="avatar"
                  />
                )}
                <div className="truncate rtl:ml-auto ltr:mr-auto">
                  {selectedOption.key}
                </div>
              </>
            ) : (
              <div className="text-gray-500 font-normal overflow-hidden text-ellipsis whitespace-nowrap">
                {placeholder}
              </div>
            )}
            <DropDownIcon
              className="h-5 w-5 -me-2 flex-shrink-0"
              aria-hidden="true"
            />
          </Menu.Button>
        )}
      </div>

      {!disabled && (
        <Transition
          as={"div"}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
          className="origin-top-start py-1 max-h-80 overflow-y-auto absolute start-0 mt-2 min-w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-[9999]"
          ref={filterRef}
          data-cy="dropdownContainer"
          onScroll={onScrollHandler}
        >
          <Menu.Items>
            {options.map(({ key, value, image }, idx) => {
              return (
                <Menu.Item data-cy="dropdownItem" key={`option-${idx}-${key}`}>
                  {({ active }) => (
                    <button
                      data-cy-value={value}
                      type="button"
                      onClick={() => onClickItem(value)}
                      className={classNames(
                        active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                        "flex items-center px-4 py-2 text-sm",
                        "w-full",
                        hasImage && "gap-3"
                      )}
                      title={key}
                    >
                      <>
                        {!!hasImage && (
                          <UserProfilePictureAvatar
                            profilePictureUrl={image}
                            className="w-6 h-6 flex-shrink-0"
                            alt="avatar"
                          />
                        )}
                        <div className="truncate">{key}</div>
                      </>
                    </button>
                  )}
                </Menu.Item>
              );
            })}
          </Menu.Items>
        </Transition>
      )}
      {!!subLabel && (
        <span className="mx-1 font-normal text-gray-400 text-xs leading-5">
          {subLabel}
        </span>
      )}
    </Menu>
  );
}
